import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { Section } from "../components/Section";
import { db, functions } from "../firebase";

type FormField = {
  id: string;
  label: string;
  type: "text" | "textarea" | "email" | "phone" | "number" | "date" | "select" | "checkbox" | "yesno";
  required?: boolean;
  options?: string[];
};

type PublicCustomForm = {
  id: string;
  title: string;
  description: string;
  isPublished: boolean;
  successMessage: string;
  fields: FormField[];
};

export function PublicForm() {
  const { slug = "" } = useParams();
  const [form, setForm] = useState<PublicCustomForm | null>(null);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        if (!db || !slug) throw new Error("Form is not available.");
        const snap = await getDoc(doc(db, "customForms", slug));
        if (!snap.exists() || snap.data()?.isPublished !== true) {
          setForm(null);
          return;
        }
        setForm({ id: snap.id, ...(snap.data() as any) });
      } catch (e: any) {
        setError(e?.message || "Failed to load form.");
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [slug]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!form || !functions) return;
    setError("");
    try {
      for (const field of form.fields) {
        if (field.required && !values[field.id]) {
          setError(`${field.label} is required.`);
          return;
        }
      }
      const submitForm = httpsCallable(functions, "submitCustomFormSubmission");
      await submitForm({
        formId: form.id,
        responses: values,
      });
      setSubmitted(true);
    } catch (e: any) {
      setError(e?.message || "Failed to submit form.");
    }
  }

  if (loading) return <PageHeader eyebrow="Loading" title="Loading form">Please wait while this form loads.</PageHeader>;
  if (!form) return <PageHeader eyebrow="Unavailable" title="Form not available">{error || "This form is not currently accepting responses."}</PageHeader>;

  if (submitted) {
    return (
      <PageHeader eyebrow="Submitted" title="Thank you">
        {form.successMessage || "Your response has been submitted."}
      </PageHeader>
    );
  }

  return (
    <>
      <PageHeader eyebrow="Zion Classical Academy" title={form.title}>
        {form.description}
      </PageHeader>
      <Section title="Submit Response">
        {error ? <p className="notice-light">{error}</p> : null}
        <form className="inquiry-form" onSubmit={submit}>
          {form.fields.map((field) => (
            <label key={field.id}>
              {field.label}{field.required ? " *" : ""}
              {field.type === "textarea" ? (
                <textarea rows={4} value={String(values[field.id] || "")} onChange={(e) => setValues((prev) => ({ ...prev, [field.id]: e.target.value }))} />
              ) : field.type === "select" ? (
                <select value={String(values[field.id] || "")} onChange={(e) => setValues((prev) => ({ ...prev, [field.id]: e.target.value }))}>
                  <option value="">Choose...</option>
                  {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              ) : field.type === "checkbox" ? (
                <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input type="checkbox" checked={values[field.id] === true} onChange={(e) => setValues((prev) => ({ ...prev, [field.id]: e.target.checked }))} />
                  <span>Yes</span>
                </span>
              ) : field.type === "yesno" ? (
                <select value={String(values[field.id] || "")} onChange={(e) => setValues((prev) => ({ ...prev, [field.id]: e.target.value }))}>
                  <option value="">Choose...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              ) : (
                <input
                  type={field.type === "phone" ? "tel" : field.type}
                  value={String(values[field.id] || "")}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.id]: e.target.value }))}
                />
              )}
            </label>
          ))}
          <button type="submit">Submit</button>
        </form>
      </Section>
    </>
  );
}
